import type { Vehicle } from './useVehicle';
import type { DeliveryNote } from './useDeliveryNote';
import type { ID } from './types';
import type { Area } from './useDeliveryAreaCode';
import useDeliveryAreaCode from './useDeliveryAreaCode';

const { DEFAULT_AREA } = useDeliveryAreaCode();

export type DeliveryStatus = 'PENDING' | 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER' | 'PREPARE_TO_DELIVER' | 'ON_THE_WAY' | 'NOT_SUCCESS' | 'SOME_SUCCESS' | 'SUCCESS' | 'HUB' | 'CANCEL';
export type DeliveryType = 'NORMAL' | 'LEASED';
export type TempDeliveryStatus = 'WAITING_TO_CONFIRM' | 'CONFIRMED' | 'REJECTED';

export interface Package {
  id: ID | number;
  name: string;
  code?: string;
  width: number | string;
  length: number | string;
  height: number | string;
  count: number;
  // due to backend using quantity as field name, so we need to added in type
  quantity?: number;
  weight: number | string;
  unit: string;
  price: number;
  product?: any;
  deliveryFee: number;
  estimateTotalDeliveryFee?: number;

  areaDeliveryFee?: number;
  areaDeliveryFeeByWeight?: number;
  areaDeliveryFeeByVolume?: number;
  deliveryFeePerUnit: number;

  itemGroup?: any;
  productId?: ID;
}
interface DeliveryLog {
  id: ID;
  delivery?: Delivery;
  status?: DeliveryStatus;
  hub?: any;
  createdAt?: string;
  deliveryNote?: DeliveryNote;
}

interface Address {
  address: string;
  subdistrict: any;
  district: any;
  province: any;
  zipcode: string;
  telephone: string;
}
export interface Delivery {
  id: ID;
  company?: any;
  customerCode?: string;
  customerName?: string;
  contactName?: string;
  contactTelephone?: string;
  orderNumber?: string;
  address: Address;
  senderCode?: string;
  senderName?: string;
  senderAddress: Address;
  deliveryArea?: string;
  deliveryFee?: number;
  deliveryDiscount?: number;
  deliveryDate?: string;
  completedDate?: string;
  daysToComplete?: number;
  orderDate?: string;
  urgentLevel?: string;
  paymentType?: string;
  remarks?: string;
  status?: string;
  packages: Package[];
  deliveryNote?: DeliveryNote;
  totalWeight?: number;
  trackingCode?: string;
  createdAt?: string;
  updatedAt?: string;
  deliveryLogs?: DeliveryLog[];
  invoice?: any;
  debtor?: any;
  lat?: number;
  long?: number;
  computedLatLong?: { lat: number; long: number };
  area?: Area | null;
  satisfaction?: number;
  hub?: any;
  failureReason?: string;
  createdBy?: any;
  lastUpdatedBy?: any;
  signatureUrl?: string;
  images?: any[];
  order?: number;
  destinationHub?: any;
  deliveryNoteLogs?: DeliveryNote[];
  targetDate?: string;
  deliveryCode?: string;
  originHub?: any;
  finalHub?: any;
  duration?: number;
  type?: DeliveryType;
  reservedVehicle?: Vehicle;
}

const DEFAULT_ADDRESS: Address = {
  address: '',
  subdistrict: {},
  district: {},
  province: {},
  zipcode: '',
  telephone: '',
};

export const DEFAULT_DELIVERY: Delivery = {
  id: '',
  company: null,
  customerCode: '',
  customerName: '',
  contactName: '',
  contactTelephone: '',
  orderNumber: '',
  address: { ...DEFAULT_ADDRESS },
  senderCode: '',
  senderName: '',
  senderAddress: { ...DEFAULT_ADDRESS },
  deliveryArea: '',
  deliveryFee: 0,
  deliveryDiscount: 0,
  deliveryDate: '',
  completedDate: '',
  daysToComplete: 0,
  orderDate: '',
  urgentLevel: '',
  paymentType: '',
  remarks: '',
  status: '',
  packages: [],
  deliveryNote: undefined,
  totalWeight: 0,
  trackingCode: '',
  createdAt: '',
  updatedAt: '',
  deliveryLogs: [],
  invoice: '',
  debtor: '',
  lat: 0,
  long: 0,
  computedLatLong: { lat: 0, long: 0 },
  area: null,
  satisfaction: 3,
  hub: undefined,
  failureReason: '',
  createdBy: '',
  lastUpdatedBy: '',
  signatureUrl: '',
  images: [],
  order: 0,
  destinationHub: undefined,
  deliveryNoteLogs: [],
  targetDate: '',
  deliveryCode: '',
  originHub: '',
  finalHub: '',
  duration: 0,
  type: 'NORMAL',
  reservedVehicle: undefined,
};

function sumPackagesPrice(packages: Package[]) {
  let price = 0;
  for (let i = 0; i < packages.length; i += 1) price += packages[i].price;
  return price;
}

export const deliveryTimeColor = {
  day1: '#134889',
  day2: '#009E4A',
  day3: '#FAAE1B',
  day4: '#F36D27',
  day7: '#FF2626',
};
export const deliveryTimeColorHue = {
  day1: '#a3c9f7',
  day2: '#8fffc3',
  day3: '#ffda91',
  day4: '#fca374',
  day7: '#ff9696',
};

export default function useDelivery() {
  const english: (DeliveryStatus | string)[] = [
    'PENDING',
    // CommonDeliveryStatusSelectorStatus will map on here
    'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
    'PREPARE_TO_DELIVER',
    'ON_THE_WAY',
    'NOT_SUCCESS',
    'SOME_SUCCESS',
    'HUB',
    'SUCCESS',
    'CANCEL',
  ];

  const thai = [
    'รอรับเข้าระบบ', // 0
    // CommonDeliveryStatusSelectorStatus will map on here
    'รายการยืนยันแล้ว', // 1
    'รายการเตรียมส่ง', // 2
    'รายการอยู่ระหว่างจัดส่ง', // 3
    'รายการส่งไม่สำเร็จ', // 4
    'รายการส่งสำเร็จบางรายการ', // 5
    'รายการถึงศูนย์กระจายสินค้า', // 6
    'รายการส่งสำเร็จ', // 7
    'ยกเลิกรายการ', // 8
  ];

  const translate = (status: string) => thai[english.indexOf(status as DeliveryStatus)];
  const shipmentStatusColor = [
    '#9E9E9E', '#CDDC39', '#3F51B5', '#FF9700', '#03A9F3', '#4CAF52', '#F44236', '#9C28AF', '#009788', '#607D8B', '#FEC107',
  ];

  const shipmentStatusDashboardColorMap = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-500';
      case 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER':
        return 'bg-yellow-500';
      case 'CANCEL':
        return 'bg-gray-300';
      case 'PREPARE_TO_DELIVER':
        return 'bg-yellow-500';
      case 'ON_THE_WAY':
        return 'bg-yellow-500';
      case 'SUCCESS':
        return 'bg-green-500';
      case 'NOT_SUCCESS':
        return 'bg-red-500';
      case 'SOME_SUCCESS':
        return 'bg-orange-500';
      case 'ORDER_RECEIVE':
        return 'bg-green-500';
      case 'REVISE':
        return 'bg-blue-400';
      case 'HUB':
        return 'bg-yellow-500';
      default: return '';
    }
  };

  const shipmentStatusColorMap = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-500';
      case 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER':
        return 'bg-accept-shipment';
      case 'CANCEL':
        return 'bg-gray-300';
      case 'PREPARE_TO_DELIVER':
        return 'bg-yellow-500';
      case 'ON_THE_WAY':
        return 'bg-yellow-500';
      case 'SUCCESS':
        return 'bg-success-shipment';
      case 'NOT_SUCCESS':
        return 'bg-not-success-shipment';
      case 'SOME_SUCCESS':
        return 'bg-orange-500';
      case 'ORDER_RECEIVE':
        return 'bg-green-500';
      case 'REVISE':
        return 'bg-blue-400';
      case 'HUB':
        return 'bg-yellow-500';
      default: return '';
    }
  };

  const shipmentStatusClassMap = (status: string) => {
    const color = shipmentStatusColorMap(status);
    switch (status) {
      case 'PENDING':
        return `block text-white rounded-xl text-sm py-1 text-center ${color}`;
      case 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      case 'CANCEL':
        return `block text-gray-600 rounded-xl text-sm py-1 text-center ${color}`;
      case 'PREPARE_TO_DELIVER':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      case 'ON_THE_WAY':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      case 'SUCCESS':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      case 'NOT_SUCCESS':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      case 'SOME_SUCCESS':
        return `block text-white rounded-xl text-sm py-1 text-center ${color}`;
      case 'ORDER_RECEIVE':
        return `block text-yellow-900 rounded-xl text-sm py-1 text-center ${color}`;
      case 'REVISE':
        return `block text-white rounded-xl text-sm py-1 text-center ${color}`;
      case 'HUB':
        return `block text-black rounded-xl text-sm py-1 text-center ${color}`;
      default: return '';
    }
  };

  const mapStatusToTracking = (status: string): number => {
    switch (status) {
      case 'PENDING_ORDER_CONFIRMATION':
      case 'ORDER_CONFIRMATION':
      case 'PENDING':
      case 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER':
        return 1;
      case 'PREPARE_TO_DELIVER':
      case 'NOT_SUCCESS':
      case 'SOME_SUCCESS':
        return 2;
      case 'ON_THE_WAY':
        return 3;
      case 'ORDER_RECEIVE':
      case 'WAITING_PAYMENT':
      case 'SUCCESS':
      case 'DONE':
        return 4;
      default:
        return 0;
    }
  };

  const type: DeliveryType[] = ['NORMAL', 'LEASED'];
  const typeDisplay = ['ทั่วไป', 'เหมาเที่ยว'];

  function typeTranslate(str: string | DeliveryType) {
    const index = type.indexOf(str as DeliveryType);
    if (index > -1) return typeDisplay[index];
    return str;
  }

  const tempDeliveryStatus: TempDeliveryStatus[] = ['WAITING_TO_CONFIRM', 'CONFIRMED', 'REJECTED'];
  const tempDeliveryStatusDisplay = ['รอยืนยัน', 'ยืนยันแล้ว', 'ปฏิเสธ'];
  function tempDeliveryStatusClassMap(status: TempDeliveryStatus) {
    const base = 'block font-bold rounded-xl text-xs py-1 text-center';
    switch (status) {
      case 'WAITING_TO_CONFIRM':
        return `${base} text-white bg-yellow-500`;
      case 'CONFIRMED':
        return `${base} text-white bg-green-500`;
      case 'REJECTED':
        return `${base} text-black bg-gray-500`;
      default:
        return `${base} text-black bg-gray-500`;
    }
  }
  function tempDeliveryStatusTranslate(status: TempDeliveryStatus) {
    const index = tempDeliveryStatus.indexOf(status);
    if (index > -1) return tempDeliveryStatusDisplay[index];
    return status;
  }

  function findFarthestDeliveryStatus(deliveryStatuses: DeliveryStatus[]) {
    const deliveryStatusesIndex = deliveryStatuses.map((status) => english.indexOf(status));
    const maxStep = Math.max(...deliveryStatusesIndex);
    return english[maxStep];
  }

  return {
    sumPackagesPrice,
    english,
    thai,
    translate,
    shipmentStatusClassMap,
    shipmentStatusColorMap,
    shipmentStatusDashboardColorMap,
    mapStatusToTracking,

    type,
    typeDisplay,
    typeTranslate,

    tempDeliveryStatus,
    tempDeliveryStatusDisplay,
    tempDeliveryStatusClassMap,
    tempDeliveryStatusTranslate,

    shipmentStatusColor,

    findFarthestDeliveryStatus,
  };
}
